import { StoryblokComponent } from "@made-people/grease-storyblok";
import {
  getStoriesFromGlobalStoryblokStoriesByComponentType,
  getStoryFromGlobalStoryblokStoriesBySlug,
} from "./global-story-functions";
import { StoryblokStoryContentResponseV1 } from "@made-people/nestjs-bff-service";
import { createContext, useContext } from "react";

export interface WithGlobalStoryProps {
  globalStoryblokStories?: StoryblokStoryContentResponseV1[];
  additionalProps?: Record<string, unknown>;
}

type StoryblokStoryLookupConfiguration =
  | { lookupType: "slug"; slug: string }
  | { lookupType: "componentType"; componentType: string };

export const withGlobalStory = (
  lookupConfig: StoryblokStoryLookupConfiguration,
  transformStory?: (
    story: StoryblokStoryContentResponseV1["content"],
  ) => StoryblokStoryContentResponseV1["content"],
) => {
  const EnhancedComponent = (props: WithGlobalStoryProps) => {
    if (!props.globalStoryblokStories) {
      return null;
    }

    let stories: StoryblokStoryContentResponseV1["content"][] | undefined =
      undefined;

    if (lookupConfig.lookupType === "componentType") {
      // Use the provided utility for componentType lookup
      stories = getStoriesFromGlobalStoryblokStoriesByComponentType(
        lookupConfig.componentType,
        props.globalStoryblokStories,
      );
    } else if (lookupConfig.lookupType === "slug") {
      // Filter by slug manually
      const matchedStory = getStoryFromGlobalStoryblokStoriesBySlug(
        lookupConfig.slug,
        props.globalStoryblokStories,
      );
      if (matchedStory) {
        stories = [matchedStory];
      }
    }

    if (!stories || stories.length === 0) {
      return null;
    }

    if (stories.length > 1) {
      console.warn(
        `Found more than one story when looking up by ${lookupConfig.lookupType} for value "${lookupConfig.lookupType}". Using the first one.`,
      );
    }

    const story = stories[0];
    const transformedStory = transformStory ? transformStory(story) : story;

    return (
      <StoryblokComponent
        blok={{ ...transformedStory, ...props.additionalProps }}
      />
    );
  };

  EnhancedComponent.displayName = `GlobalStoryblokStory`;

  return EnhancedComponent;
};

export const globalStoryblokStoriesContext = createContext<
  StoryblokStoryContentResponseV1[]
>([]);

export const useGlobalStoryblokStories = () =>
  useContext(globalStoryblokStoriesContext);
